<template>
  <div>
    <v-card flat>
      <v-data-table
        :headers="headers"
        :items="filteredMedicationClasses"
        :items-per-page="10"
        class="elevation-0 custom-data-table"
        :no-data-text="noDataText"
        :height="tableHeight"
        fixed-header
        :loading="tableLoading"
        density="compact"
      >
        <template v-slot:top>
          <div class="d-flex justify-space-between align-center pa-4">
            <span class="text-h5">Medication Classes</span>
            <div class="d-flex align-center">
              <v-select
                v-model="selectedCategoryId"
                :items="categoryOptions"
                :item-title="(c) => formatMedicationCategoryName(c.medication_category_name)"
                item-value="medication_category_id"
                label="Category"
                clearable
                hide-details
                variant="outlined"
                density="compact"
                style="width: 180px; margin-right: 16px"
              />
              <v-text-field
                v-model="search"
                prepend-inner-icon="mdi-magnify"
                label="Search"
                single-line
                hide-details
                variant="outlined"
                density="compact"
                style="width: 350px; margin-right: 16px"
              ></v-text-field>
              <v-btn
                color="primary"
                prepend-icon="mdi-plus"
                @click="openCreateDialog"
              >
                Create Medication Class
              </v-btn>
            </div>
          </div>
        </template>

        <template v-slot:[`item.medication_category_name`]="{ item }">
          {{ formatMedicationCategoryName(item.medication_category_name) }}
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-btn
            icon
            size="small"
            variant="text"
            color="primary"
            @click="editMedicationClass(item)"
          >
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn
            icon
            size="small"
            variant="text"
            color="error"
            @click="deleteMedicationClass(item)"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <CreateSubCategoryDialog
      v-model="showCreateDialog"
      @created="handleMedicationClassCreated"
    />

    <EditSubCategoryDialog
      v-model="showEditDialog"
      :subCategory="selectedMedicationClass"
      @updated="handleMedicationClassUpdated"
    />

    <DeleteSubCategoryDialog
      v-model="showDeleteDialog"
      :subCategory="selectedMedicationClass"
      @deleted="handleMedicationClassDeleted"
    />
  </div>
</template>

<script>
import CreateSubCategoryDialog from "../Dialog/CreateSubCategoryDialog.vue";
import EditSubCategoryDialog from "../Dialog/EditSubCategoryDialog.vue";
import DeleteSubCategoryDialog from "../Dialog/DeleteSubCategoryDialog.vue";
import GetMedicationCategories from "@/mixins/Medication/GetMedicationCategories";
import { formatMedicationCategoryName } from "@/utils/medicationCategoryLabel";
import GetMedicationClasses from "@/mixins/Medication/GetMedicationClasses";

export default {
  name: "MedicationSubCategoryCard",
  mixins: [GetMedicationCategories, GetMedicationClasses],
  components: {
    CreateSubCategoryDialog,
    EditSubCategoryDialog,
    DeleteSubCategoryDialog,
  },
  data() {
    return {
      showCreateDialog: false,
      showEditDialog: false,
      showDeleteDialog: false,
      tableLoading: false,
      selectedMedicationClass: null,
      search: "",
      selectedCategoryId: null,
      windowHeight: window.innerHeight,
      categories: [],
      headers: [
        { title: "Class Name", key: "class_name", sortable: true },
        {
          title: "Category",
          key: "medication_category_name",
          sortable: true,
        },
        { title: "Actions", key: "actions", sortable: false, align: "center" },
      ],
    };
  },
  computed: {
    noDataText() {
      return "No medication classes found. Click 'Create Medication Class' to add one.";
    },
    tableHeight() {
      return this.windowHeight - 280;
    },
    categoryOptions() {
      return this.categories;
    },
    filteredMedicationClasses() {
      let list = this.medicationClassesList;

      if (this.selectedCategoryId) {
        list = list.filter(
          (item) =>
            String(item.medication_category_id) ===
            String(this.selectedCategoryId)
        );
      }

      if (!this.search) {
        return list;
      }

      const searchTerm = this.search.toLowerCase().trim();
      return list.filter((item) => {
        const className = (item.class_name || "").toLowerCase();
        const categoryName = (formatMedicationCategoryName(item.medication_category_name) || "").toLowerCase();
        return (
          className.includes(searchTerm) || categoryName.includes(searchTerm)
        );
      });
    },
  },
  async mounted() {
    this.updateWindowHeight();
    window.addEventListener("resize", this.updateWindowHeight);
    await this.loadCategories();
    await this.loadMedicationClasses();
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.updateWindowHeight);
  },
  methods: {
    formatMedicationCategoryName,
    async loadCategories() {
      try {
        await this.fetchMedicationCategories();
        this.categories = this.medicationCategoriesList.map((item) => ({
          medication_category_id: item.medication_category_id,
          medication_category_name: item.medication_category_name,
        }));
      } catch (error) {
        console.error("Error loading medication categories:", error);
      }
    },
    async loadMedicationClasses() {
      this.tableLoading = true;
      try {
        await this.fetchMedicationClasses();
      } catch (error) {
        console.error("Error loading medication classes:", error);
      } finally {
        this.tableLoading = false;
      }
    },
    updateWindowHeight() {
      this.windowHeight = window.innerHeight;
    },
    onTabActivated() {
      this.loadCategories();
      this.loadMedicationClasses();
    },
    openCreateDialog() {
      this.showCreateDialog = true;
    },
    async handleMedicationClassCreated() {
      await this.loadMedicationClasses();
    },
    editMedicationClass(item) {
      this.selectedMedicationClass = { ...item };
      this.showEditDialog = true;
    },
    async handleMedicationClassUpdated() {
      await this.loadMedicationClasses();
      this.selectedMedicationClass = null;
    },
    deleteMedicationClass(item) {
      this.selectedMedicationClass = { ...item };
      this.showDeleteDialog = true;
    },
    async handleMedicationClassDeleted() {
      await this.loadMedicationClasses();
      this.selectedMedicationClass = null;
    },
  },
};
</script>
